'use client';

import { useEffect, useRef, useState } from 'react';

export interface Currency {
  code: string;
  name: string;
  symbol: string;
}

const CURRENCIES: Currency[] = [
  { code: 'SEK', name: 'Svenska kronor', symbol: 'kr' },
  { code: 'EUR', name: 'Euro', symbol: '€' },
  { code: 'USD', name: 'US Dollar', symbol: '$' },
  { code: 'NOK', name: 'Norska kronor', symbol: 'kr' },
  { code: 'DKK', name: 'Danska kronor', symbol: 'kr' },
  { code: 'GBP', name: 'British Pound', symbol: '£' },
  { code: 'CHF', name: 'Swiss Franc', symbol: 'CHF' },
];

interface CurrencySelectorProps {
  value: string;
  onChange: (currency: string) => void;
  label?: string;
  className?: string;
  showArrow?: boolean;
  localStorageKey?: string;
}

export default function CurrencySelector({ value, onChange, label, className = '', showArrow = true, localStorageKey = 'currencyPreference' }: CurrencySelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const initializedFromStorageRef = useRef(false);
  
  
  const selectedCurrency = CURRENCIES.find(c => c.symbol === value) || CURRENCIES[0];
  
  const handleSelect = (currency: Currency) => {
    onChange(currency.symbol);
    try {
      if (localStorageKey) {
        localStorage.setItem(localStorageKey, currency.symbol);
      }
    } catch {}
    setIsOpen(false);
  };

  // Initialize from localStorage once
  useEffect(() => {
    if (initializedFromStorageRef.current) return;
    initializedFromStorageRef.current = true;
    try {
      if (!localStorageKey) return;
      const stored = localStorage.getItem(localStorageKey);
      if (stored && stored !== value) {
        onChange(stored);
      }
    } catch {}
  }, [localStorageKey, onChange, value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!containerRef.current) return;
      const target = event.target as Node;
      if (isOpen && target && !containerRef.current.contains(target)) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label}
        </label>
      )}
      
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={label ? `${label}: ${selectedCurrency.name}` : `Currency selector: ${selectedCurrency.name}`}
      >
        <span className="flex items-center gap-2">
          <span className="font-medium">{selectedCurrency.symbol}</span>
          <span className="text-gray-500 text-xs">({selectedCurrency.code})</span>
        </span>
        {showArrow && (
          <svg
            className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        )}
      </button>
      
      {isOpen && (
        <div 
          className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto"
          role="listbox"
          aria-label="Currency options"
        >
          {CURRENCIES.map((currency) => (
            <button
              key={currency.code}
              type="button"
              onClick={() => handleSelect(currency)}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 flex items-center justify-between ${
                selectedCurrency.code === currency.code ? 'bg-blue-50 text-blue-700' : 'text-gray-900'
              }`}
              role="option"
              aria-selected={selectedCurrency.code === currency.code}
              aria-label={`Select ${currency.name} (${currency.symbol})`}
            >
              <span className="flex items-center gap-2">
                <span className="font-medium">{currency.symbol}</span>
                <span className="text-gray-500 text-xs">({currency.code})</span>
              </span>
              <span className="text-xs text-gray-500">{currency.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
